/**
 * Web Bluetooth Service for scanning BLE devices
 * Chrome browser only - Web Bluetooth API is not available in all browsers
 */

class BluetoothService {
  constructor() {
    this.isSupported = 'bluetooth' in navigator;
  }

  /**
   * Check if Web Bluetooth is supported
   */
  isBluetoothSupported() {
    return this.isSupported;
  }

  /**
   * Check if we're in Chrome (Web Bluetooth works best in Chrome)
   */
  isChrome() {
    return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  }

  /**
   * Check if page is served over HTTPS (required for Web Bluetooth)
   */
  isSecureContext() {
    return window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  }

  /**
   * Check if Bluetooth is available (supported, secure context, and enabled)
   */
  async isBluetoothAvailable() {
    if (!this.isSupported) {
      return { available: false, reason: 'Web Bluetooth API is not supported in this browser. Please use Chrome.' };
    }

    if (!this.isSecureContext()) {
      return { available: false, reason: 'Bluetooth requires HTTPS. Please use https:// instead of http://' };
    }

    try {
      const availability = await navigator.bluetooth.getAvailability();
      if (!availability) {
        return { available: false, reason: 'Bluetooth is not available on this device. Please enable Bluetooth.' };
      }
      return { available: true };
    } catch (error) {
      return { available: false, reason: 'Could not determine Bluetooth availability.' };
    }
  }

  /**
   * Request Bluetooth device (for manual connection)
   */
  async requestDevice(options = {}) {
    if (!this.isSupported) {
      throw new Error('Web Bluetooth API is not supported in this browser. Please use Chrome.');
    }

    try {
      // Common BLE service UUIDs for temperature/humidity sensors
      const commonSensorServices = [
        'battery_service',                    // 0x180F - Battery Service
        'device_information',                 // 0x180A - Device Information
        'environmental_sensing',              // 0x181A - Environmental Sensing
        '00001800-0000-1000-8000-00805f9b34fb', // Generic Access
        '00001801-0000-1000-8000-00805f9b34fb', // Generic Attribute
        '0000180a-0000-1000-8000-00805f9b34fb', // Device Information
        '0000180f-0000-1000-8000-00805f9b34fb', // Battery Service
        '0000181a-0000-1000-8000-00805f9b34fb', // Environmental Sensing
        // KBeacon specific services
        '0000fff0-0000-1000-8000-00805f9b34fb', // KBeacon Service
        '0000fee7-0000-1000-8000-00805f9b34fb', // KBeacon Config
        // ELA/PUCK services
        '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service
        // Common custom sensor services
        '00001523-1212-efde-1523-785feabcd123', // Custom sensor service
        '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip transparent service
      ];

      const device = await navigator.bluetooth.requestDevice({
        // Use filters instead of acceptAllDevices for better compatibility
        filters: [
          { services: ['battery_service'] },
          { services: ['environmental_sensing'] },
          { namePrefix: 'KBeacon' },
          { namePrefix: 'KBPro' },
          { namePrefix: 'Sensor' },
          { namePrefix: 'ELA' },
          { namePrefix: 'PUCK' },
          { namePrefix: 'BLog' },
          { namePrefix: 'T201' },
          { namePrefix: 'T301' },
          { namePrefix: 'Chill' },
        ],
        optionalServices: commonSensorServices,
        ...options,
      });

      return device;
    } catch (error) {
      console.error('Bluetooth request error:', error);

      if (error.name === 'NotFoundError') {
        throw new Error('No device selected');
      } else if (error.name === 'SecurityError') {
        throw new Error('Bluetooth permission denied. Make sure you are using HTTPS and have granted permission.');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Bluetooth is not supported or not enabled on this device.');
      } else if (error.message && error.message.includes('HTTPS')) {
        throw new Error('Bluetooth requires HTTPS. Please use https:// instead of http://');
      }

      throw new Error(error.message || 'Failed to connect to Bluetooth device');
    }
  }

  /**
   * Scan for BLE devices
   * Note: Web Bluetooth API doesn't support continuous scanning like native apps
   * We use requestDevice which shows a device picker
   * The filtering happens when user selects a device
   */
  async scanForDevices(onDeviceFound, duration = 10000) {
    if (!this.isSupported) {
      throw new Error('Web Bluetooth API is not supported in this browser. Please use Chrome.');
    }

    if (!this.isChrome()) {
      throw new Error('Bluetooth scanning works best in Chrome browser. Please use Chrome for this feature.');
    }

    // Web Bluetooth API limitation: We can't scan continuously
    // requestDevice shows a device picker - we'll filter after selection
    // This is a limitation of the Web Bluetooth API
    throw new Error('Use requestDevice() instead - Web Bluetooth API does not support continuous scanning');
  }

  /**
   * Check if device is a sensor device (filter unwanted devices)
   * Based on React Native filtering logic from BluetoothScanScreen
   * Filters out large unwanted devices like phones, headphones, etc.
   */
  isSensorDevice(device) {
    if (!device) {
      return false;
    }

    const name = (device.name || '').toUpperCase();
    const id = (device.id || '').toUpperCase();

    // If no name and ID doesn't look like a sensor, skip
    if (!name && !id) {
      return false;
    }

    // Filter out common non-sensor devices (based on React Native filtering)
    const unwantedPatterns = [
      'APPLE',
      'IPHONE',
      'IPAD',
      'MACBOOK',
      'AIRPODS',
      'BEATS',
      'SAMSUNG',
      'GALAXY',
      'WATCH',
      'FITBIT',
      'GARMIN',
      'POLAR',
      'SONY',
      'BOSE',
      'JBL',
      'SPEAKER',
      'KEYBOARD',
      'MOUSE',
      'TRACKPAD',
      'PRINTER',
      'HP',
      'CANON',
      'EPSON',
      'LAPTOP',
      'COMPUTER',
      'PC',
      'DESKTOP',
      'MONITOR',
      'TV',
      'TELEVISION',
      'SMART',
      'PHONE',
      'TABLET',
    ];

    // Skip unwanted devices
    if (unwantedPatterns.some(pattern => name.includes(pattern) || id.includes(pattern))) {
      return false;
    }

    // Include sensor-like devices (based on React Native device detection)
    const sensorPatterns = [
      'KBEACON',
      'KBPRO',
      'SENSOR',
      'SENSORPUSH',
      'ELA',
      'PUCK',
      'BLOG',
      'BULOG',
      'T201',
      'T301',
      'T306',
      'THOF',
      'TH0F',
      'SIGMAWIT',
      'CHILL',
      'VERIFY',
      'CHILLVERIFY',
    ];

    // Check if name or ID matches sensor patterns
    const matchesSensor = sensorPatterns.some(pattern => name.includes(pattern) || id.includes(pattern));

    // Also check if ID looks like a MAC address (sensor devices often use MAC as ID)
    const looksLikeMac = /^([0-9A-F]{2}[:-]){5}([0-9A-F]{2})$/i.test(id) || /^[0-9A-F]{12}$/i.test(id);

    return matchesSensor || (looksLikeMac && name.length > 0);
  }

  /**
   * Try to read MAC address from device GATT characteristics
   * @param {BluetoothDevice} device - The connected Bluetooth device
   * @returns {Promise<string|null>} MAC address if found, null otherwise
   */
  async readMacAddress(device) {
    try {
      if (!device.gatt) {
        console.warn('Device GATT not available');
        return null;
      }

      // Connect to device if not already connected
      let server;
      if (!device.gatt.connected) {
        console.log('Connecting to device to read MAC...');
        server = await device.gatt.connect();
      } else {
        server = device.gatt;
      }

      // Try to read from Device Information Service (0x180A)
      try {
        const deviceInfoService = await server.getPrimaryService('device_information');

        // Try System ID characteristic (0x2A23) - often contains MAC
        try {
          const systemIdChar = await deviceInfoService.getCharacteristic('00002a23-0000-1000-8000-00805f9b34fb');
          const value = await systemIdChar.readValue();

          // System ID is 8 bytes, MAC is typically first 6 bytes
          if (value.byteLength >= 6) {
            const macBytes = [];
            for (let i = 0; i < 6; i++) {
              const byte = value.getUint8(i).toString(16).toUpperCase().padStart(2, '0');
              macBytes.push(byte);
            }
            const mac = macBytes.join(':');
            console.log('MAC address from System ID:', mac);
            return mac;
          }
        } catch (err) {
          console.log('System ID characteristic not available:', err.message);
        }

        // Try Serial Number String (0x2A25) - might contain MAC
        try {
          const serialChar = await deviceInfoService.getCharacteristic('00002a25-0000-1000-8000-00805f9b34fb');
          const value = await serialChar.readValue();
          const decoder = new TextDecoder('utf-8');
          const serial = decoder.decode(value);
          console.log('Serial Number:', serial);

          // Check if it looks like a MAC address
          if (/^([0-9A-F]{2}[:-]){5}([0-9A-F]{2})$/i.test(serial) || /^[0-9A-F]{12}$/i.test(serial)) {
            const mac = serial.length === 12 ? serial.match(/.{2}/g).join(':') : serial;
            console.log('MAC address from Serial Number:', mac);
            return mac.toUpperCase();
          }
        } catch (err) {
          console.log('Serial Number characteristic not available:', err.message);
        }
      } catch (err) {
        console.log('Device Information Service not available:', err.message);
      }

      // Try KBeacon specific service (0xFFF0)
      try {
        const kbeaconService = await server.getPrimaryService('0000fff0-0000-1000-8000-00805f9b34fb');
        // KBeacon might have MAC in a specific characteristic
        const characteristics = await kbeaconService.getCharacteristics();
        console.log(`Found ${characteristics.length} KBeacon characteristics`);

        for (const char of characteristics) {
          try {
            const value = await char.readValue();
            if (value.byteLength === 6) {
              const macBytes = [];
              for (let i = 0; i < 6; i++) {
                const byte = value.getUint8(i).toString(16).toUpperCase().padStart(2, '0');
                macBytes.push(byte);
              }
              const mac = macBytes.join(':');
              console.log('Potential MAC from KBeacon service:', mac);
              return mac;
            }
          } catch (err) {
            // Skip characteristics we can't read
          }
        }
      } catch (err) {
        console.log('KBeacon service not available:', err.message);
      }

      // Try Generic Access Service (0x1800)
      try {
        const genericAccessService = await server.getPrimaryService('generic_access');
        const characteristics = await genericAccessService.getCharacteristics();

        for (const char of characteristics) {
          try {
            const value = await char.readValue();
            // Look for 6-byte values that might be MAC
            if (value.byteLength === 6) {
              const macBytes = [];
              for (let i = 0; i < 6; i++) {
                const byte = value.getUint8(i).toString(16).toUpperCase().padStart(2, '0');
                macBytes.push(byte);
              }
              const mac = macBytes.join(':');
              console.log('Potential MAC from Generic Access:', mac);
              return mac;
            }
          } catch (err) {
            // Skip characteristics we can't read
          }
        }
      } catch (err) {
        console.log('Generic Access service not available:', err.message);
      }

      console.log('Could not read MAC address from device characteristics');
      return null;

    } catch (error) {
      console.error('Error reading MAC address:', error);
      return null;
    }
  }

  /**
   * Parse QR code data (same logic as React Native)
   */
  parseQRCodeData(data) {
    let sensorName = 'Unknown Sensor';
    let sensorId = data;
    let macAddress = undefined;
    let serialNumber = undefined;
    let deviceType = undefined;

    try {
      // Check if it's a URL format
      if (data.startsWith('http')) {
        console.log('URL format detected:', data);
        const url = new URL(data);
        sensorId = url.searchParams.get('id') || url.pathname.split('/').pop() || data.slice(-8);
        macAddress = url.searchParams.get('mac') || undefined;
        console.log('Extracted sensor ID from URL:', sensorId);
      }
      // Check if it's JSON format
      else if (data.startsWith('{')) {
        console.log('JSON format detected');
        const jsonData = JSON.parse(data);
        console.log('Parsed JSON:', jsonData);
        sensorId = jsonData.id || jsonData.deviceId || jsonData.serial || data.slice(-8);
        sensorName = jsonData.name || `Sensor ${sensorId.slice(-4)}`;
        macAddress = jsonData.mac || jsonData.macAddress;
        serialNumber = jsonData.serial || jsonData.serialNumber;
        console.log('Extracted from JSON - ID:', sensorId, 'Name:', sensorName);
      }
      // Check if it's a KBeacon MAC/SERIAL format
      else if (data.includes('MAC:') && data.includes('SERIAL:')) {
        console.log('KBeacon MAC/SERIAL format detected');
        const macMatch = data.match(/MAC:([A-F0-9]{12})/);
        const serialMatch = data.match(/SERIAL:(\d+)/);

        macAddress = macMatch ? macMatch[1] : undefined;
        serialNumber = serialMatch ? serialMatch[1] : undefined;

        console.log('Extracted MAC:', macAddress, 'SERIAL:', serialNumber);

        sensorId = data.trim();
        deviceType = 'kbeacon';

        if (serialNumber) {
          sensorName = `KBPro_${serialNumber}`;
        } else if (macAddress) {
          sensorName = `KBPro_${macAddress.slice(-4)}`;
        } else {
          sensorName = `KBPro_${data.slice(-4)}`;
        }
        console.log('Generated KBeacon sensor name:', sensorName, 'ID:', sensorId);
      }
      // Check if it's a simple ID format (could be MAC address)
      else {
        console.log('Simple format detected');
        sensorId = data.trim();

        // Check if it looks like a MAC address
        if (/^[A-F0-9]{12}$/i.test(sensorId) || /^([A-F0-9]{2}[:-]){5}[A-F0-9]{2}$/i.test(sensorId)) {
          macAddress = sensorId;
          sensorName = `Sensor ${sensorId.slice(-4)}`;
        } else {
          const shortId = sensorId.slice(-4);
          sensorName = `KBPro_${shortId}`; // Use underscore for consistency with React Native
        }
        console.log('Generated sensor name:', sensorName, 'ID:', sensorId);
      }
    } catch (error) {
      console.error('Error parsing QR data:', error);
      sensorId = data;
      sensorName = `Sensor ${data.slice(-4)}`;
    }

    // Format MAC address if it's in compact format (12 hex chars without colons)
    let formattedMacAddress = macAddress;
    if (macAddress && /^[A-F0-9]{12}$/i.test(macAddress)) {
      formattedMacAddress = macAddress.match(/.{2}/g)?.join(':').toUpperCase();
      console.log(`Formatted MAC: ${macAddress} -> ${formattedMacAddress}`);
    }

    return {
      sensorId,
      sensorName,
      macAddress: formattedMacAddress,
      serialNumber,
      deviceType: deviceType || 'kbeacon', // Default to kbeacon
    };
  }
}

export default new BluetoothService();

